import {AUD} from '../../../constants/GlobalConstants';
import {
  AddToCartEvent,
  AnalyticsEvent,
  EventName,
  PurchaseEvent,
} from '../events';

// Google Analytics was done first, so we currently consider it as the
// "canonical" model for events. We then transform these events into the
// Meta Analytics model.

// Meta App Events reference:
// https://developers.facebook.com/docs/app-events/reference

// https://developers.facebook.com/docs/meta-pixel/reference (Standard Events)
export enum AppEventName {
  // Standard Meta events
  // AddPaymentInfo = 'AddPaymentInfo',
  AddToCart = 'AddToCart',
  // AddToWishlist = 'AddToWishlist',
  // CompleteRegistration = 'CompleteRegistration',
  // Contact = 'Contact',
  // CustomizeProduct = 'CustomizeProduct',
  // Donate = 'Donate',
  // FindLocation = 'FindLocation',
  // InitiateCheckout = 'InitiateCheckout',
  // Lead = 'Lead',
  Purchase = 'Purchase',
  // Schedule = 'Schedule',
  // Search = 'Search',
  // StartTrial = 'StartTrial',
  // Subscribe = 'Subscribe',
  // ViewContent = 'ViewContent',
}

// https://developers.facebook.com/docs/meta-pixel/reference (Object Properties)
export enum AppEventParamName {
  content_category = 'content_category',
  content_ids = 'content_ids',
  content_name = 'content_name',
  content_type = 'content_type',
  contents = 'contents',
  currency = 'currency',
  num_items = 'num_items',
  predicted_ltv = 'predicted_ltv',
  search_string = 'search_string',
  status = 'status',
  value = 'value',
}

type ContentAppEventParam = {
  id: string; // International Article Number (EAN) when applicable
  quantity: number;
};

// https://developers.facebook.com/docs/meta-pixel/reference (Object Properties)
type AppEventParams = {
  // Category of the page/product
  [AppEventParamName.content_category]: string;
  // Product IDs associated with the event, such as SKUs (e.g. ['ABC123', 'XYZ789'])
  [AppEventParamName.content_ids]: string[] | number[];
  // Name of the page/product
  [AppEventParamName.content_name]: string;
  // Either product or product_group based on the content_ids or contents being passed.
  // If the IDs being passed in content_ids or contents parameter are IDs of products
  // then the value should be product. If product group IDs are being passed, then the
  // value should be product_group
  [AppEventParamName.content_type]: 'product' | 'product_group';
  // An array of JSON objects that contains the quantity and the International Article
  // Number (EAN) when applicable, or other product or content identifier(s). id and
  // quantity are the required fields. e.g.
  // [{'id': 'ABC123', 'quantity': 2}, {'id': 'XYZ789', 'quantity': 2}]
  [AppEventParamName.contents]: ContentAppEventParam;
  // The currency for the value specified
  [AppEventParamName.currency]: 'AUD';
  // Used with InitiateCheckout event. The number of items when checkout was initiated
  [AppEventParamName.num_items]: number;
  // Predicted lifetime value of a subscriber as defined by the advertiser and expressed
  // as an exact value.
  [AppEventParamName.predicted_ltv]: number;
  // Used with the Search event. The string entered by the user for the search
  [AppEventParamName.search_string]: string;
  // Used with the CompleteRegistration event, to show the status of the registration
  [AppEventParamName.status]: boolean;
  // The value of a user performing this event to the business
  [AppEventParamName.value]: number;
};

export type WebMetaAnalyticsEventParameters = {
  [key in AppEventParamName]?: AppEventParams[keyof AppEventParams];
} & {
  // I added this just for easy transformation in
  // utils/analytics/meta/transformations.ts (`gaEvent2Meta`)
  [key: string]: AppEventParams[keyof AppEventParams];
};

export type WebMetaAnalyticsEvent = {
  eventName: AppEventName | string; // !!!
  parameters: WebMetaAnalyticsEventParameters;
};

export type WebGa2MetaAnalyticsEventsTransformationFn<
  X extends AnalyticsEvent,
  Y extends WebMetaAnalyticsEvent,
> = (event: X) => Y;

// type ParamTypes = string | string[] | number;

// When you see `ContentID` below, the API also usually offers `ContentType` and
// `Content` as alternatives. We only use `ContentID` for now.

export interface WebMetaAddToCartEvent extends WebMetaAnalyticsEvent {
  eventName: AppEventName.AddToCart;
  parameters: Pick<
    AppEventParams,
    // | AppEventParamName.contents // not used here
    | AppEventParamName.content_ids
    | AppEventParamName.content_name
    | AppEventParamName.content_type
    | AppEventParamName.currency
    | AppEventParamName.value
  >;
}

// When a purchase is made or checkout flow is completed.
// A person has finished the purchase or checkout flow and lands on thank you
// or confirmation page.
export interface WebMetaPurchaseEvent extends WebMetaAnalyticsEvent {
  eventName: AppEventName.Purchase;
  parameters: Pick<
    AppEventParams,
    | AppEventParamName.content_ids
    // | AppEventParamName.content_name // not used here
    // | AppEventParamName.content_type // not used here
    | AppEventParamName.currency // required
    | AppEventParamName.num_items
    | AppEventParamName.value // required
  >;
}

// transformations

export const gaAddToCartEvent2Meta: WebGa2MetaAnalyticsEventsTransformationFn<
  AddToCartEvent,
  WebMetaAddToCartEvent
> = (event: AddToCartEvent) => ({
  // Only one item in `event.parameters.items` is currently always passed in
  // this event in this codebase
  eventName: AppEventName.AddToCart,
  parameters: {
    [AppEventParamName.content_ids]: [event.parameters.items[0].item_id],
    [AppEventParamName.content_name]: event.parameters.items[0].item_name,
    [AppEventParamName.content_type]: 'product',
    [AppEventParamName.currency]: AUD,
    [AppEventParamName.value]: event.parameters.value,
  },
});

export const gaPurchaseEvent2Meta: WebGa2MetaAnalyticsEventsTransformationFn<
  PurchaseEvent,
  WebMetaPurchaseEvent
> = ({parameters}: PurchaseEvent) => ({
  // Only one item in `event.parameters.items` is currently always passed in
  // this event in this codebase
  eventName: AppEventName.Purchase,
  parameters: {
    [AppEventParamName.content_ids]: parameters.items.map(
      item => item.item_id || '',
    ),
    [AppEventParamName.currency]: AUD,
    [AppEventParamName.num_items]: parameters.items.length,
    [AppEventParamName.value]: parameters.value,
  },
});

export const gaEvent2Meta = (event: AnalyticsEvent): WebMetaAnalyticsEvent =>
  event;

export type WebGa2MetaAnalyticsEventsTransformationSuperFn =
  WebGa2MetaAnalyticsEventsTransformationFn<
    AnalyticsEvent,
    WebMetaAnalyticsEvent
  >;

type Ga2MetaAnalyticsEventsTransformationsTable = {
  [key in EventName]?: WebGa2MetaAnalyticsEventsTransformationSuperFn;
};

export const Ga2MetaAnalyticsEventsTransformations: Ga2MetaAnalyticsEventsTransformationsTable =
  {
    [EventName.AddToCart]:
      gaAddToCartEvent2Meta as WebGa2MetaAnalyticsEventsTransformationSuperFn,
    [EventName.Purchase]:
      gaPurchaseEvent2Meta as WebGa2MetaAnalyticsEventsTransformationSuperFn,
  };
